import React, { useState, useEffect, lazy, Suspense } from 'react';
import { 
  Card, Row, Col, Spin, message, Button,
  Space, Typography, FloatButton, Empty, Tabs, Modal
} from 'antd';
import { 
  ReloadOutlined, MobileOutlined,
  DashboardOutlined, BarChartOutlined, LineChartOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';

// Hooks
import useDashboardData from './hooks/useDashboardData';
import useInsights from './hooks/useInsights';
import useCustomWidgets from './hooks/useCustomWidgets';

// Components
import EnhancedMetricCard from './components/EnhancedMetricCard';
import EnhancedControlPanel from './components/EnhancedControlPanel';
import TrendChart from './components/TrendChart';
import ChannelDistribution from './components/ChannelDistribution';
import ActionableInsights from './components/ActionableInsights';
import { WidgetLibrary } from './components/CustomizableWidget';
import DraggableWidgetGrid from './components/DraggableWidget';
import DashboardTemplates from './components/DashboardTemplates';

// Lazy load heavy components
const ROIGauge = lazy(() => import('./components/ROIGauge'));
const WeeklyHeatmap = lazy(() => import('./components/WeeklyHeatmap'));
const QuickActions = lazy(() => import('./components/QuickActions'));
const PerformanceReport = lazy(() => import('./components/PerformanceReport'));

// Lazy load widgets
const RevenueForecastWidget = lazy(() => import('./widgets/RevenueForecastWidget'));
const CampaignTrackerWidget = lazy(() => import('./widgets/CampaignTrackerWidget'));
const CompetitorAnalysisWidget = lazy(() => import('./widgets/CompetitorAnalysisWidget'));
const SocialMetricsWidget = lazy(() => import('./widgets/SocialMetricsWidget'));

const { Title } = Typography;
const { TabPane } = Tabs;

const Dashboard = () => {
  // Custom hooks
  const {
    loading,
    brands,
    selectedBrand,
    setSelectedBrand,
    dateRange,
    setDateRange,
    data,
    summaryData,
    channelData,
    brandMetrics,
    comparisonData,
    comparisonLoading,
    fetchComparisonData,
    refetch
  } = useDashboardData();

  const { generateActionableInsights } = useInsights(summaryData, channelData);

  // Custom widgets hook
  const {
    customWidgets,
    addWidget,
    removeWidget,
    updateWidgetSettings,
    reorderWidgets
  } = useCustomWidgets(selectedBrand);

  // Local state
  const [isMobile, setIsMobile] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [templateModalVisible, setTemplateModalVisible] = useState(false);
  
  // Enhanced Dashboard State - AllBrandsMetrics style
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [comparisonMode, setComparisonMode] = useState(false);
  const [comparisonPeriod, setComparisonPeriod] = useState('previous_month');
  const [comparisonDateRange, setComparisonDateRange] = useState(null);
  const [isCustomComparison, setIsCustomComparison] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedAdvisor, setSelectedAdvisor] = useState(null);
  const [visibleMetrics, setVisibleMetrics] = useState(['REVENUE', 'ROI', 'COST(ALL)', 'COS']);
  const [cacheEnabled, setCacheEnabled] = useState(true);

  // Sync selectedBrand with selectedBrands array
  useEffect(() => {
    if (selectedBrand && !selectedBrands.includes(selectedBrand)) {
      setSelectedBrands([selectedBrand]);
    }
  }, [selectedBrand, selectedBrands]);

  // Handle multi-brand selection
  const handleBrandsChange = (brandIds) => {
    setSelectedBrands(brandIds);
    if (brandIds.length === 1) {
      setSelectedBrand(brandIds[0]);
    } else if (brandIds.length === 0) {
      setSelectedBrand(null);
    }
  };

  // Fetch comparison data with custom dates support
  const fetchComparisonDataWithDates = async (period, customDates = null) => {
    if (period === 'custom' && customDates) {
      // Use custom date range
      return await fetchComparisonData(period, customDates);
    } else if (period !== 'custom') {
      // Use predefined period
      return await fetchComparisonData(period);
    }
    return null;
  };

  // Handle comparison mode toggle
  const handleComparisonToggle = async () => {
    const newComparisonMode = !comparisonMode;
    setComparisonMode(newComparisonMode);
    
    if (newComparisonMode) {
      message.info('Karşılaştırma moduna geçildi - veriler yükleniyor...');
      if (isCustomComparison && comparisonDateRange) {
        await fetchComparisonDataWithDates('custom', comparisonDateRange);
      } else if (!isCustomComparison) {
        await fetchComparisonDataWithDates(comparisonPeriod);
      }
    } else {
      message.info('Normal görünüme geçildi');
    }
  };

  // Handle comparison period change
  const handleComparisonPeriodChange = async (newPeriod) => {
    if (newPeriod === 'custom') {
      setIsCustomComparison(true);
      setComparisonPeriod(newPeriod);
      // Don't fetch data yet, wait for custom date selection
    } else {
      setIsCustomComparison(false);
      setComparisonPeriod(newPeriod);
      setComparisonDateRange(null);
      if (comparisonMode) {
        message.info('Karşılaştırma dönemi değiştirildi - veriler yükleniyor...');
        await fetchComparisonDataWithDates(newPeriod, null);
      }
    }
  };

  // Handle custom comparison date range change
  const handleComparisonDateRangeChange = async (dates) => {
    if (!dates || dates.length !== 2) {
      setComparisonDateRange(null);
      return;
    }

    const [start, end] = dates;
    if (!start || !end) {
      setComparisonDateRange(null);
      return;
    }

    const startDate = dayjs(start).startOf('day');
    const endDate = dayjs(end).endOf('day');
    
    // Validate date range
    const minDate = dayjs().subtract(5, 'year').startOf('day');
    const maxDate = dayjs().subtract(1, 'day').endOf('day');

    if (startDate.isBefore(minDate) || endDate.isAfter(maxDate)) {
      message.warning('Lütfen geçerli bir tarih aralığı seçin (son 5 yıl içinde)');
      return;
    }

    setComparisonDateRange([startDate, endDate]);
    
    if (comparisonMode) {
      message.info('Karşılaştırma tarihleri değiştirildi - veriler yükleniyor...');
      await fetchComparisonDataWithDates('custom', [startDate, endDate]);
    }
  };

  // Calculate comparison data based on real API data
  const calculateComparisonData = (currentData, metric) => {
    if (!comparisonMode || !currentData || !comparisonData) return {};

    const previousData = comparisonData.summary[metric];
    if (!previousData) return {};

    // Calculate percentage change
    const change = previousData.current > 0 
      ? ((currentData.current - previousData.current) / previousData.current) * 100 
      : 0;

    // Different targets per metric and brand
    const targetValues = {
      'ROI': selectedBrand === 1 ? 4.5 : selectedBrand === 2 ? 3.8 : 4.0,
      'REVENUE': currentData.current * 1.15,
      'COST(ALL)': currentData.current * 0.85,
      'COS': currentData.current * 0.8
    };

    return {
      previous: previousData.current,
      target: targetValues[metric] || currentData.current * 1.1,
      benchmark: currentData.current * 0.98,
      change: change,
      period: comparisonPeriod,
      periodLabel: comparisonData.startDate && comparisonData.endDate
        ? `${comparisonData.startDate} - ${comparisonData.endDate}`
        : {
          'previous_week': 'Önceki Hafta',
          'previous_month': 'Önceki Ay', 
          'previous_quarter': 'Önceki Çeyrek',
          'previous_year': 'Önceki Yıl',
          'same_period_last_year': 'Geçen Yılın Aynı Dönemi',
          'custom': 'Özel Tarih Aralığı'
        }[comparisonPeriod] || 'Önceki Dönem'
    };
  };

  // Responsive breakpoints
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);


  const handleDateChange = (dates) => {
    if (!dates) return;

    const [start, end] = dates;
    if (!start || !end) return;

    const startDate = dayjs(start).startOf('day');
    const endDate = dayjs(end).endOf('day');

    const minDate = dayjs().subtract(5, 'year').startOf('day');
    const maxDate = dayjs().add(1, 'year').endOf('day');

    if (startDate.isBefore(minDate) || endDate.isAfter(maxDate)) {
      message.warning('Lütfen geçerli bir tarih aralığı seçin');
      return;
    }

    setDateRange([startDate, endDate]);
  };

  // Widget management
  const handleAddWidget = (widget) => {
    addWidget(widget);
    message.success(`${widget.title} eklendi`);
  };

  const handleRemoveWidget = (widgetId) => {
    removeWidget(widgetId);
    message.info('Widget kaldırıldı');
  };

  const handleWidgetSettingsChange = (widgetId, newSettings) => {
    updateWidgetSettings(widgetId, newSettings);
  };

  // Handle template selection
  const handleTemplateSelect = (template) => {
    // Clear existing widgets
    customWidgets.forEach(widget => removeWidget(widget.id));
    
    // Add template widgets
    template.widgets.forEach(widgetConfig => {
      const availableWidget = availableWidgets.find(w => w.key === widgetConfig.key);
      if (availableWidget) {
        addWidget({
          ...availableWidget,
          settings: { ...availableWidget.defaultSettings, ...widgetConfig.settings }
        });
      }
    });

    message.success(`${template.name} template'i uygulandı`);
  };

  // Available widgets for customization
  const availableWidgets = [
    {
      key: 'revenue-forecast',
      title: 'Gelir Tahmini',
      icon: '📊',
      description: 'Ai destekli gelir tahmin modeli',
      component: () => (
        <Suspense fallback={<Spin />}>
          <RevenueForecastWidget data={data} summaryData={summaryData} />
        </Suspense>
      ),
      defaultSettings: { size: 'large', autoRefresh: true, refreshInterval: 300 }
    },
    {
      key: 'campaign-tracker',
      title: 'Kampanya Takip',
      icon: '🎯',
      description: 'Aktif kampanya durumu',
      component: () => (
        <Suspense fallback={<Spin />}>
          <CampaignTrackerWidget />
        </Suspense>
      ),
      defaultSettings: { size: 'large', autoRefresh: false }
    },
    {
      key: 'competitor-analysis',
      title: 'Rakip Analizi',
      icon: '🔍',
      description: 'Sektör karşılaştırması',
      component: () => (
        <Suspense fallback={<Spin />}>
          <CompetitorAnalysisWidget />
        </Suspense>
      ),
      defaultSettings: { size: 'full', autoRefresh: false }
    },
    {
      key: 'social-metrics',
      title: 'Sosyal Medya',
      icon: '📱',
      description: 'Sosyal medya performansı',
      component: () => (
        <Suspense fallback={<Spin />}>
          <SocialMetricsWidget />
        </Suspense>
      ),
      defaultSettings: { size: 'medium', autoRefresh: true, refreshInterval: 600 }
    }
  ];


  // Overview cards
  const renderOverviewCards = () => {
    if (!summaryData || Object.keys(summaryData).length === 0) {
      return (
        <Card style={{ marginBottom: 16 }}>
          <Empty 
            description="Seçilen tarih aralığında veri bulunamadı" 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      );
    }

    // Filter metrics based on visibility settings
    const filteredMetrics = Object.entries(summaryData)
      .filter(([metric]) => visibleMetrics.includes(metric))
      .slice(0, isMobile ? 2 : visibleMetrics.length);

    // If no visible metrics or less than needed, show top priority ones
    if (filteredMetrics.length === 0) {
      const topPriorityOrder = ['REVENUE', 'ROI', 'COST(ALL)', 'COS'];
      const topMetrics = topPriorityOrder
        .filter(metric => summaryData[metric])
        .map(metric => [metric, summaryData[metric]])
        .slice(0, isMobile ? 2 : 4);
      filteredMetrics.push(...topMetrics);
    }

    return (
      <Row gutter={[8, 8]} style={{ marginBottom: 16 }}>
        {filteredMetrics.map(([metric, data]) => (
          <Col xs={12} sm={12} lg={6} key={metric}>
            <EnhancedMetricCard 
              metric={metric} 
              data={data} 
              isMobile={isMobile}
              isEditable={true}
              showComparison={comparisonMode}
              comparisonData={calculateComparisonData(data, metric)}
              onEdit={(metric) => {
                message.info(`${metric} metriği düzenleme özelliği yakında eklenecek`);
              }}
            />
          </Col>
        ))}
      </Row>
    );
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ 
      padding: isMobile ? '8px' : '16px',
      backgroundColor: '#f5f5f5',
      minHeight: '100vh'
    }}>
      {/* Page Header */}
      <div style={{ marginBottom: isMobile ? 12 : 24 }}>
        {!isMobile && (
          <Title level={2} style={{ margin: 0, marginBottom: 16, color: '#1890ff' }}>
            <DashboardOutlined /> Analytics Dashboard
          </Title>
        )}
        
        {/* Enhanced Control Panel */}
        <EnhancedControlPanel
          brands={brands}
          selectedBrands={selectedBrands}
          onBrandsChange={handleBrandsChange}
          dateRange={dateRange}
          onDateRangeChange={handleDateChange}
          onRefresh={refetch}
          loading={loading || comparisonLoading}
          isMobile={isMobile}
          comparisonMode={comparisonMode}
          onComparisonToggle={handleComparisonToggle}
          comparisonPeriod={comparisonPeriod}
          onComparisonPeriodChange={handleComparisonPeriodChange}
          isCustomComparison={isCustomComparison}
          comparisonDateRange={comparisonDateRange}
          onComparisonDateRangeChange={handleComparisonDateRangeChange}
          searchText={searchText}
          onSearchChange={setSearchText}
          selectedAdvisor={selectedAdvisor}
          onAdvisorChange={setSelectedAdvisor}
          visibleMetrics={visibleMetrics}
          onMetricVisibilityChange={setVisibleMetrics}
          availableMetrics={[
            { key: 'REVENUE', label: 'Gelir' },
            { key: 'ROI', label: 'ROI' },
            { key: 'COST(ALL)', label: 'Toplam Maliyet' },
            { key: 'COS', label: 'COS' },
            { key: 'META COST (ALL)', label: 'Meta Maliyeti' },
            { key: 'BOOST ADS', label: 'Boost Reklamları' },
            { key: 'INSTAGRAM FOLLOWER', label: 'Instagram Takipçi' },
            { key: 'TRANSACTION', label: 'İşlem' },
            { key: 'AVARAGE ORDER VALUE', label: 'Ortalama Sipariş Değeri' }
          ]}
          cacheEnabled={cacheEnabled}
          onCacheToggle={setCacheEnabled}
          onExport={() => {
            // Export functionality
          }}
        />
      </div>

      {!selectedBrand ? (
        <Card style={{ textAlign: 'center', margin: '50px 0' }}>
          <Empty 
            description="Lütfen bir marka seçin" 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </Card>
      ) : (
        <div id="dashboard-content">
          {/* Tab Navigation */}
          <Tabs 
            activeKey={activeTab} 
            onChange={setActiveTab}
            style={{ marginBottom: 16 }}
          >
            <TabPane 
              tab={<span><LineChartOutlined /> Genel Bakış</span>} 
              key="overview"
            >
              {/* Overview Cards */}
              {renderOverviewCards()}

              {/* Main Charts */}
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} lg={16}>
                  <Card title="📈 Genel Trend" style={{ height: isMobile ? '320px' : '400px' }}>
                    <TrendChart 
                      data={data} 
                      height={isMobile ? 250 : 320}
                      comparisonMode={comparisonMode}
                      comparisonPeriod={comparisonPeriod}
                      dateRange={dateRange}
                      comparisonData={comparisonData}
                    />
                  </Card>
                </Col>
                <Col xs={24} lg={8}>
                  <Card 
                    title="💰 Harcama Dağılımı" 
                    style={{ height: isMobile ? '320px' : '400px' }}
                    styles={{ 
                      body: {
                        height: 'calc(100% - 56px)', 
                        display: 'flex',
                        flexDirection: 'column'
                      }
                    }}
                  >
                    <ChannelDistribution 
                      channelData={channelData} 
                      data={data}
                      comparisonMode={comparisonMode}
                      comparisonPeriod={comparisonPeriod}
                      dateRange={dateRange}
                      comparisonData={comparisonData}
                    />
                  </Card>
                </Col>
              </Row>

              {/* Insights */}
              <ActionableInsights insights={generateActionableInsights()} />

              {/* Quick Actions */}
              <Suspense fallback={<Spin />}>
                <QuickActions 
                  insights={generateActionableInsights()} 
                  selectedBrand={selectedBrand}
                  summaryData={summaryData}
                  dateRange={dateRange}
                />
              </Suspense>
            </TabPane>

            <TabPane 
              tab={<span><BarChartOutlined /> Detaylı Analiz</span>} 
              key="detailed"
            >
              <Row gutter={[16, 16]}>
                <Col xs={24} lg={12}>
                  <Suspense fallback={<Spin />}>
                    <ROIGauge summaryData={summaryData} />
                  </Suspense>
                </Col>
                <Col xs={24} lg={12}>
                  <Suspense fallback={<Spin />}>
                    <WeeklyHeatmap 
                      data={data}
                      comparisonMode={comparisonMode}
                      comparisonPeriod={comparisonPeriod}
                      dateRange={dateRange}
                      comparisonData={comparisonData}
                    />
                  </Suspense>
                </Col>
              </Row>

              <Suspense fallback={<Spin />}>
                <PerformanceReport summaryData={summaryData} />
              </Suspense>
            </TabPane>

            <TabPane 
              tab={<span><DashboardOutlined /> Özel Dashboard</span>} 
              key="custom"
            >
              <div style={{ marginBottom: 16 }}>
                <Space>
                  <WidgetLibrary 
                    onAddWidget={handleAddWidget}
                    availableWidgets={availableWidgets}
                  />
                  <Button 
                    type="default"
                    onClick={() => setTemplateModalVisible(true)}
                  >
                    📋 Template Seç
                  </Button>
                  {customWidgets.length > 0 && (
                    <Button 
                      danger 
                      onClick={() => {
                        Modal.confirm({
                          title: 'Tüm widget\'ları kaldır?',
                          content: 'Bu işlem geri alınamaz. Tüm özel widget\'larınız silinecek.',
                          okText: 'Evet, Kaldır',
                          cancelText: 'İptal',
                          onOk: () => {
                            customWidgets.forEach(widget => removeWidget(widget.id));
                            message.success('Tüm widget\'lar kaldırıldı');
                          }
                        });
                      }}
                    >
                      Tümünü Temizle
                    </Button>
                  )}
                </Space>
              </div>

              {customWidgets.length === 0 ? (
                <Card style={{ textAlign: 'center', padding: '60px 20px' }}>
                  <Empty 
                    description="Henüz özel widget eklenmemiş"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  >
                    <p style={{ marginTop: 16 }}>
                      Yukarıdaki "Yeni Widget Ekle" butonunu kullanarak dashboard'unuzu özelleştirin
                    </p>
                  </Empty>
                </Card>
              ) : (
                <DraggableWidgetGrid
                  widgets={customWidgets.map(widget => {
                    // Re-attach component based on widget key
                    const availableWidget = availableWidgets.find(w => w.key === widget.key);
                    return {
                      ...widget,
                      component: availableWidget ? availableWidget.component : () => <div>Widget not found</div>
                    };
                  })}
                  onReorder={reorderWidgets}
                  onRemove={handleRemoveWidget}
                  onSettingsChange={handleWidgetSettingsChange}
                  availableMetrics={brandMetrics}
                />
              )}
            </TabPane>
          </Tabs>
        </div>
      )}

      {/* Dashboard Templates Modal */}
      <DashboardTemplates
        visible={templateModalVisible}
        onClose={() => setTemplateModalVisible(false)}
        onSelectTemplate={handleTemplateSelect}
      />

      {/* Mobile Float Action Button */}
      {isMobile && (
        <FloatButton.Group>
          <FloatButton
            icon={<ReloadOutlined />}
            tooltip="Verileri Yenile"
            onClick={refetch}
          />
          <FloatButton
            icon={<MobileOutlined />}
            tooltip="Mobile View"
            type="primary"
          />
        </FloatButton.Group>
      )}
    </div>
  );
};

export default React.memo(Dashboard);